// Push Notification Service
import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { doc, setDoc } from 'firebase/firestore';
import { messaging, db } from '../config/firebase';

class PushNotificationService {
  private vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  private isEnabled = false;

  constructor() {
    // Only enable push notifications if VAPID key is configured
    this.isEnabled = !!this.vapidKey && this.vapidKey !== 'YOUR_ACTUAL_VAPID_KEY_HERE';
    if (!this.isEnabled) {
      console.warn('Push notifications disabled: VAPID key not configured. Please configure VITE_FIREBASE_VAPID_KEY in .env file.');
    }
  }

  async requestPermission(): Promise<boolean> {
    if (!this.isEnabled) {
      console.warn('Push notifications are disabled - VAPID key not configured');
      return false;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('Notification permission granted.');
        return true;
      } else {
        console.log('Notification permission denied.');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  async getToken(): Promise<string | null> {
    if (!this.isEnabled) {
      return null;
    }
    
    if (!messaging) {
      console.warn('Firebase Messaging not initialized');
      return null;
    }

    try {
      const token = await getToken(messaging, { vapidKey: this.vapidKey });
      console.log('FCM Token obtained successfully');
      return token;
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  setupForegroundMessageHandling(): void {
    if (!messaging) {
      console.warn('Firebase Messaging not initialized');
      return;
    }

    onMessage(messaging, (payload: MessagePayload) => {
      console.log('Message received in foreground:', payload);
      
      // Display custom notification
      this.showNotification(
        payload.notification?.title || 'New Message',
        {
          body: payload.notification?.body || 'You have a new notification',
          icon: '/pwa-192x192.png',
          badge: '/pwa-192x192.png',
          tag: payload.data?.type || 'default',
          data: payload.data || {},
          requireInteraction: true
        }
      );
    });
  }

  private async showNotification(title: string, options: NotificationOptions): Promise<void> {
    if ('serviceWorker' in navigator && 'Notification' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(title, options);
      } catch (error) {
        console.error('Error showing notification:', error);
        // Fallback to browser notification
        if (Notification.permission === 'granted') {
          new Notification(title, options);
        }
      }
    }
  }

  async initialize(): Promise<void> {
    if (!this.isEnabled) {
      console.info('Push notifications are disabled. Configure VAPID key to enable.');
      return;
    }
    
    const permissionGranted = await this.requestPermission();
    if (permissionGranted) {
      const token = await this.getToken();
      if (token) {
        // Store token for sending notifications from backend
        this.saveTokenToDatabase(token);
        this.setupForegroundMessageHandling();
      }
    }
  }

  private async saveTokenToDatabase(token: string): Promise<void> {
    try {
      const userId = this.getCurrentUserId();
      if (!userId) {
        console.warn('No authenticated user to save FCM token');
        return;
      }

      await setDoc(doc(db, 'users', userId), {
        fcmToken: token,
        tokenUpdated: new Date()
      }, { merge: true });

      console.log('FCM token saved to database');
    } catch (error) {
      console.error('Error saving FCM token to database:', error);
    }
  }

  private getCurrentUserId(): string | null {
    // This will need to be integrated with your auth system
    // For now, return null if no user is authenticated
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.uid || null;
  }

  // Utility methods for different notification types
  async sendMatchNotification(matchedUserName: string): Promise<void> {
    await this.showNotification(`New Match! 🔥`, {
      body: `You matched with ${matchedUserName}!`,
      icon: '/pwa-192x192.png',
      tag: 'match',
      data: { type: 'match', userName: matchedUserName },
      actions: [
        { action: 'view', title: 'View Profile', icon: '/pwa-192x192.png' },
        { action: 'message', title: 'Send Message', icon: '/pwa-192x192.png' }
      ]
    });
  }

  async sendMessageNotification(senderName: string, message: string): Promise<void> {
    await this.showNotification(`Message from ${senderName}`, {
      body: message.length > 50 ? `${message.substring(0, 47)}...` : message,
      icon: '/pwa-192x192.png',
      tag: 'message',
      data: { type: 'message', senderName },
      actions: [
        { action: 'reply', title: 'Reply', icon: '/pwa-192x192.png' },
        { action: 'view', title: 'View Chat', icon: '/pwa-192x192.png' }
      ]
    });
  }

  async sendMedicationReminderNotification(medicationName: string): Promise<void> {
    await this.showNotification(`💊 Medication Reminder`, {
      body: `Time to take your ${medicationName}`,
      icon: '/pwa-192x192.png',
      tag: 'medication',
      data: { type: 'medication', medicationName },
      requireInteraction: true,
      actions: [
        { action: 'taken', title: 'Mark as Taken', icon: '/pwa-192x192.png' },
        { action: 'snooze', title: 'Remind in 30min', icon: '/pwa-192x192.png' }
      ]
    });
  }

  async sendScreeningReminderNotification(): Promise<void> {
    await this.showNotification(`🏥 Health Screening Reminder`, {
      body: `It's time for your regular STI screening`,
      icon: '/pwa-192x192.png',
      tag: 'screening',
      data: { type: 'screening' },
      requireInteraction: true,
      actions: [
        { action: 'schedule', title: 'Schedule Appointment', icon: '/pwa-192x192.png' },
        { action: 'view', title: 'View Health', icon: '/pwa-192x192.png' }
      ]
    });
  }
}

export default new PushNotificationService();